import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';

import {
  PagedResponse,
  GenericGetFilterDto,
  GetMeDTO,
  UserListByTeamDto,
  UserListResponseDto,
  UserAvailabilityListResponseDto,
  UsersFilterDto,
  AgentsStatusSummaryResponseDto,
} from '@bandido/shared/dtos';

import { QueryKeys } from '../common/types';
import { client } from '../common/services';

interface useGetAllUsersProps extends UsersFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetAllUsers = (params?: useGetAllUsersProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, roles, agencyId } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getAllUsers, currentPage, pageSize, search, roles, agencyId],
    queryFn: async (): Promise<PagedResponse<UserListResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<UserListResponseDto>, any> = await client.get('user', {
          params: {
            currentPage,
            pageSize,
            search,
            roles,
            agencyId,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetUsersByTokensAgency = (params: useGetUsersByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getUsersByTokensAgency, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<UserListByTeamDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<UserListByTeamDto>, any> = await client.get(
          'user/agency/token',
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );
        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface useGetUsersByAgencyProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetUsersByAgency = (params: useGetUsersByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  return useQuery({
    enabled: Boolean(agencyId),
    queryKey: [QueryKeys.getUsersByAgency, agencyId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<UserListByTeamDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<UserListByTeamDto>, any> = await client.get(
          `user/agency/${agencyId}`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );
        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetUsersByAccess = (params: useGetUsersByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  return useQuery({
    enabled: Boolean(agencyId),
    queryKey: [QueryKeys.getUsersByAgency, agencyId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<UserListByTeamDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<UserListByTeamDto>, any> = await client.get(
          `user/agency-access/${agencyId}`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );
        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface useGetUsersByTeamProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
  teamId?: number;
}

export const useGetUsersByTeam = (params: useGetUsersByTeamProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, teamId } = params || {};
  return useQuery({
    enabled: Boolean(teamId),
    queryKey: [QueryKeys.getUsersByTeam, teamId, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<UserListByTeamDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<UserListByTeamDto>, any> = await client.get(`user/team/${teamId}`, {
          params: {
            currentPage,
            pageSize,
            search,
          },
        });

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface UseGetUserById {
  id?: string;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetUserById = ({ id, onErrorCallback }: UseGetUserById) =>
  useQuery({
    queryKey: [QueryKeys.getUserById, id],
    queryFn: async (): Promise<GetMeDTO | undefined> => {
      try {
        const result: AxiosResponse<GetMeDTO, any> = await client.get(`user/${id}/profile`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });

export const useGetUserAgencies = ({
  onErrorCallback,
  userId,
}: {
  userId?: string;
  onErrorCallback?: (_e: AxiosError) => void;
}) =>
  useQuery({
    queryKey: [QueryKeys.getUserAgencies, userId],
    queryFn: async (): Promise<number[] | undefined> => {
      try {
        const result: AxiosResponse<number[], any> = await client.get(`user/${userId}/agencies`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(userId),
  });

interface useGetUserAvailabilityProps extends GenericGetFilterDto {
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetUsersAvailability = (params?: useGetUserAvailabilityProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback } = params || {};
  return useQuery({
    queryKey: [QueryKeys.getUserAvailability, currentPage, pageSize, search],
    queryFn: async (): Promise<PagedResponse<UserAvailabilityListResponseDto> | undefined> => {
      try {
        const result: AxiosResponse<PagedResponse<UserAvailabilityListResponseDto>, any> = await client.get(
          'agent-status',
          {
            params: {
              currentPage,
              pageSize,
              status: search,
            },
          },
        );

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

export const useUserStatusChange = (
  userId?: string,
  options?: Omit<UseMutationOptions<AxiosResponse, unknown, string, unknown>, 'mutationFn'> | undefined,
) => useMutation((status: string) => client.post(`user/${userId}/change-status`, { status }), options);

interface UseGetAgentsProps {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId?: string | string[];
  search?: string;
  pageSize?: number;
  currentPage?: number;
}

export const useGetAgents = (params?: UseGetAgentsProps) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};
  const agencyIdParam = Array.isArray(agencyId) ? agencyId.join(',') : agencyId;

  return useQuery({
    queryKey: [QueryKeys.getAgents, currentPage, pageSize, search, agencyIdParam],
    queryFn: async (): Promise<PagedResponse<UserListByTeamDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<UserListByTeamDto>, any> = await client.get(
          'user/agents',
          {
            params: {
              currentPage,
              pageSize,
              search,
              agencyId: agencyIdParam,
            },
          },
        );
        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

interface UseGetAgentsStatusSummaryProps {
  agencyIds?: string[] | number[];
  onErrorCallback?: (_e: AxiosError) => void;
  enabled?: boolean;
}

export const useGetAgentsStatusSummary = (params?: UseGetAgentsStatusSummaryProps) => {
  const { agencyIds, onErrorCallback, enabled = true } = params || {};
  const agencyIdParam = Array.isArray(agencyIds) ? agencyIds.join(',') : agencyIds;

  return useQuery({
    queryKey: [QueryKeys.getAgentsStatusSummary, agencyIdParam],
    queryFn: async (): Promise<AgentsStatusSummaryResponseDto | undefined> => {
      try {
        const response: AxiosResponse<AgentsStatusSummaryResponseDto, any> = await client.get(
          'user/agents/status-summary',
          {
            params: {
              agencyId: agencyIdParam,
            },
          },
        );
        return response.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: enabled && Boolean(agencyIdParam),
  });
};
